import { FeatureCollection, Geometry, Polygon } from '@turf/helpers'
import { Extent } from 'ol/extent'
import { apiFetch, ApiFetchError } from './api'
import Histogram from './histogram'
import { toQueryString } from './utils/url-utils'
import { OrthoMask } from './types/common'
import { method } from 'lodash'
import { toSnakeCase } from './object-utils'

type Labels = {
  showLabels: true
  labelScale: number
  numberDecimals: number
}

type NoLabels = { showLabels?: false }

type LabelStyle = Labels | NoLabels

type PlantCountStyle = {
  geometryType: string
  class: string
  measurement?: string
  compOp?: string
  breaks?: number[]
  palette: (string | null)[]
  stroke?: boolean
  markerStroke?: boolean
  fillOpacity?: number
  zoomScaleFactor?: number
  useMarkersForLargeScales?: boolean
} & LabelStyle

type PlantCountOptions = {
  bounds?: Geometry
  styles?: PlantCountStyle[]
  background?: string
}

export type MeasurementStatistics = {
  [measurement: string]: {
    min: number
    max: number
    median: number
    histogram: Histogram
    // eslint-disable-next-line camelcase
    cell_size?: number
  }
}

export type PlantCountsResponse = {
  total: number
  classes: {
    [index: string]: number
  }
  statistics: MeasurementStatistics
  type: 'Point' | 'Polygon'
}

// TS2 does not use camelcase
/* eslint-disable camelcase */
/**
 * Linear encoding means pixel values were encoded using this formula:
 *   pixel_value = data_value * scale + offset
 */
type LinearRasterEncoding = {
  method: 'linear'
  offset: number
  scale: number
}

// In the future, we could have more encodings, but for now
// we only have linear encoding.
export type RasterEncoding = LinearRasterEncoding

export type BandStatistics = {
  min: number
  max: number
  pc: [number, number]
  std: number
  histogram: [number[], number[]]
}

type Ts2LayerMetadata = {
  band_descriptions: Array<[number, string]>
  bounds: { crs: string; value: Extent }
  data_range: [number, number]
  dtype: string
  width: number
  height: number
  minzoom: number
  maxzoom: number
  raster_encoding?: RasterEncoding
  resolution: number
  histogram_resolution: number
  statistics: Record<number, BandStatistics>
}

export type LayerMetadata = Ts2LayerMetadata & {
  histogram?: Array<{ start: number; end: number; count: number }>
  histogramObject?: Histogram
  histogramResolution: number
  dtype: string
  data_range: [number, number]
  min?: number
  max?: number
} & Ts2LayerMetadata

type Ts2LineStyle = {
  'stroke-width': number
}

type Ts2MarkerStyle = {
  width: number
  stroke?: string
}

type Ts2LabelStyle = {
  scale: number
  numberDecimals: number
}

type Ts2LayerStyle = {
  class: string
  attribute?: string
  breaks: Array<{ value?: number; color: string | null }>
  scales: Array<{
    value?: number
    line?: Ts2LineStyle
    label?: Ts2LabelStyle
    marker?: Ts2MarkerStyle
  }>
}

type Ts2PlantCountConfig = {
  bounds?: Geometry
  styles?: Ts2LayerStyle[]
}
/* eslint-enable camelcase */

type LayerParams = {
  pixelSize?: number
  bounds?: Geometry
  mask?: OrthoMask
}

export default class TileServerClient {
  token: string
  ts2BaseUrl: string

  constructor(ts2BaseUrl: string, token: string) {
    this.token = token
    this.ts2BaseUrl = ts2BaseUrl
  }

  getLayerMetadata(
    layer: string,
    format: string,
    bands: string,
    params: LayerParams | undefined = undefined
  ): Promise<LayerMetadata> {
    const url =
      this.getLayerUrl(layer, format, bands) + '.json?token=' + this.token
    const queryObj: Record<string, string | number | undefined> = {
      ...scaleToQuery(params?.pixelSize),
      ...boundsToQuery(params?.bounds),
      ...maskToQuery(params?.mask),
    }
    // Enable hi definition for Plant Focus layers
    if (params?.mask && params.mask.type !== 'none') {
      queryObj.type = 'hi-def'
    }
    const query = toQueryString(queryObj)

    let fetchPromise
    if (!query || query.length < 256) {
      fetchPromise = apiFetch(url + '&' + query, { retryHttp202: true })
    } else {
      fetchPromise = apiFetch(url, {
        method: 'POST',
        retryHttp202: true,
        body: query,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
    }
    return fetchPromise
      .then(data => tileServerMetadataToInternalMetadata(data, bands, format))
      .catch(err => {
        if (err.code === 'missing_dataset') {
          throw new MissingLayerException(
            `Dataset ${layer} (${bands}/${format}) does not exist`
          )
        } else {
          throw err
        }
      })
  }

  getLayerTileUrl(
    layer: string,
    format: string,
    bands: string,
    pixelSize: number | undefined = undefined,
    mask?: OrthoMask
  ) {
    return (
      this.getLayerUrl(layer, format, bands) +
      '/tile/{z}/{x}/{y}.png?' +
      toQueryString({
        token: this.token,
        scale: pixelSize,
        ...maskToQuery(mask),
      })
    )
  }

  getLayerUrl(layer: string, format: string, bands: string) {
    return this.ts2BaseUrl + '/' + [layer, bands, format].join('/')
  }

  async getPlantCounts(
    path: string,
    options: PlantCountOptions
  ): Promise<PlantCountsResponse> {
    const id = getPlantCountId(path)
    const authParams = toQueryString({
      token: this.token,
    })
    const config = plantCountOptionsToConfig(options)

    try {
      const { config_id: configId } = await apiFetch(
        `${this.ts2BaseUrl}/plant_counts/${id}/config.json?${authParams}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(config),
        }
      )

      const data = await apiFetch(
        `${this.ts2BaseUrl}/plant_counts/${id}/${configId}.json?${authParams}`
      )

      for (const measurement in data.statistics) {
        const sourceHistogram = data.statistics[measurement].histogram
        data.statistics[measurement].histogram = sourceHistogram
          ? Histogram.fromNumPyHistogram(sourceHistogram)
          : null
      }

      return data
    } catch (e) {
      if (e instanceof ApiFetchError) {
        if (e.response.status === 404) {
          throw new MissingLayerException(
            `Plant detection result with id ${id} can't be found. It might have been automatically cleaned up.`
          )
        }
      }
      throw e
    }
  }

  async getPlantCountsArea(
    path: string,
    options: PlantCountOptions
  ): Promise<number> {
    const id = getPlantCountId(path)
    const authParams = toQueryString({
      token: this.token,
    })
    const config = plantCountOptionsToConfig(options)

    const { config_id: configId } = await apiFetch(
      `${this.ts2BaseUrl}/plant_counts/${id}/config.json?${authParams}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      }
    )

    return await apiFetch(
      `${this.ts2BaseUrl}/plant_counts/${id}/${configId}/area.json?${authParams}`
    )
  }

  async getPlantCountZones(
    path: string,
    zones: FeatureCollection<Polygon>,
    options?: { percentiles?: number[] }
  ) {
    const id = getPlantCountId(path)
    const authParams = toQueryString({
      token: this.token,
      ...(options?.percentiles
        ? { percentiles: JSON.stringify(options.percentiles) }
        : null),
    })

    const { config_id: configId } = await apiFetch(
      `${this.ts2BaseUrl}/plant_counts/${id}/config.json?${authParams}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      }
    )

    return apiFetch(
      `${this.ts2BaseUrl}/plant_counts/${id}/${configId}/zones.json?${authParams}`,
      {
        method: 'POST',
        body: JSON.stringify(zones),
        headers: { 'Content-Type': 'application/json' },
      }
    )
  }

  async getPlantCountsTileUrl(path: string, options: PlantCountOptions) {
    const id = getPlantCountId(path)
    const authParams = toQueryString({
      token: this.token,
    })
    const config = plantCountOptionsToConfig(options)

    const { config_id: configId } = await apiFetch(
      `${this.ts2BaseUrl}/plant_counts/${id}/config.json?${authParams}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      }
    )

    return `${this.ts2BaseUrl}/plant_counts/${id}/${configId}/tile/{z}/{x}/{y}.png?${authParams}`
  }

  getDerivedLayer(sourceLayer: string, params: Record<string, unknown>) {
    return apiFetch(`${this.ts2BaseUrl}/${sourceLayer}?token=${this.token}`, {
      method: 'POST',
      body: JSON.stringify(toSnakeCase(params)),
      headers: { 'Content-Type': 'application/json' },
    })
  }

  async createDensityMap(
    path: string,
    cellSizeMeters: number,
    gridRotation: number,
    options: { bounds?: Geometry } = {}
  ) {
    const id = getPlantCountId(path)
    const authParams = toQueryString({
      token: this.token,
    })
    const config = plantCountOptionsToConfig({
      ...options,
    })
    const { config_id: configId } = await apiFetch(
      `${this.ts2BaseUrl}/plant_counts/${id}/config.json?${authParams}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      }
    )

    return apiFetch(
      `${this.ts2BaseUrl}/plant_counts/${id}/${configId}/density_map/${cellSizeMeters}.json?${authParams}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ grid_rotation: gridRotation }),
      }
    )
  }

  async getPlantCountFeatures(
    path: string,
    extent: Extent
  ): Promise<FeatureCollection> {
    const id = getPlantCountId(path)
    const authParams = toQueryString({
      token: this.token,
    })
    return apiFetch(
      `${this.ts2BaseUrl}/plant_counts/${id}/features/${extent.join(
        ','
      )}.json?${authParams}`
    )
  }

  clearPlantDetectionCache(jobId: string) {
    return apiFetch(
      `${this.ts2BaseUrl}/plant_counts/${jobId}?token=${this.token}`,
      {
        method: 'DELETE',
      }
    )
  }
}

export function tileServerMetadataToInternalMetadata(
  data: Ts2LayerMetadata,
  bands: string,
  format: string
): LayerMetadata {
  if (data.statistics && data.statistics[1]) {
    const stats = data.statistics[1]
    const [counts, keys] = stats.histogram
    const histogram = Histogram.fromNumPyHistogram(stats.histogram)
    const median = histogram.median()

    if (median === undefined) {
      console.warn(
        'Could not calculate histogram median, min/max will be undefined (empty histogram?)'
      )
    }

    // Adapt to old tile server format
    return {
      ...data,
      ...stats,
      histogram: counts.map((y, i) => ({
        start: keys[i],
        end: keys[i + 1],
        count: y,
      })),
      histogramObject: histogram,
      histogramResolution: data.histogram_resolution,
      dtype: data.dtype || (bands.length === 3 ? 'uint8' : 'uint16'),
      data_range:
        data.data_range || (bands.length === 3 ? [0, 255] : [0, 65535]),
      raster_encoding:
        data.raster_encoding || // Fallback for legacy dataset with missing encoding
        (format.startsWith('_')
          ? format === '_thermal'
            ? { method: 'linear', scale: 10, offset: -data.statistics[1].min }
            : { method: 'linear', scale: 100, offset: 1 }
          : undefined),
    }
  } else if (!data || Object.keys(data).length === 0) {
    throw new EmptyLayerException('The layer is empty and has no metadata.')
  } else {
    return { ...data, histogramResolution: data.histogram_resolution }
  }
}

function getPlantCountId(path: string): string {
  if (path === 'results/plant_counts.zip') {
    return 'default'
  } else {
    const m = /^jobs\/([A-Za-z0-9-]+)/.exec(path)
    if (m) {
      return m[1]
    } else {
      throw new Error(`Unrecognized path format: "${path}"`)
    }
  }
}

const defaultMarkerStroke = '#831'

export function plantCountOptionsToConfig(
  options: PlantCountOptions
): Ts2PlantCountConfig {
  return {
    bounds: options.bounds,
    ...(options.background
      ? { 'map-config': { 'background-color': options.background } }
      : {}),
    styles:
      options.styles &&
      options.styles.map(style => {
        const fill =
          style.fillOpacity != null && style.fillOpacity > 0
            ? { fill: { 'fill-opacity': style.fillOpacity } }
            : null
        const stroke = style.stroke == null || style.stroke
        const markerStroke =
          style.markerStroke == null || style.markerStroke
            ? { stroke: defaultMarkerStroke }
            : undefined

        const { zoomScaleFactor = 1 } = style

        const showLabels = style.showLabels && style.measurement != null

        const useMarkersForLargeScales =
          style.useMarkersForLargeScales == null ||
          style.useMarkersForLargeScales

        return {
          class: style.class,
          attribute: style.measurement,
          breaks: [
            ...(style.breaks || []).map((b, i) => ({
              value: b,
              color: style.palette[i],
            })),
            { color: style.palette?.[style.breaks?.length || 0] },
          ],
          ...(style.compOp ? { 'comp-op': style.compOp } : {}),
          scales:
            style.geometryType === 'Polygon'
              ? [
                  {
                    value: 133 * zoomScaleFactor,
                    ...(stroke ? { line: { 'stroke-width': 2 } } : {}),
                    ...fill,
                    label: showLabels
                      ? {
                          scale: style.labelScale,
                          numberDecimals: style.numberDecimals,
                          dy: -10,
                        }
                      : undefined,
                  },
                  {
                    value: 533 * zoomScaleFactor,
                    ...(stroke ? { line: { 'stroke-width': 1 } } : {}),
                    ...fill,
                  },
                  ...(useMarkersForLargeScales
                    ? [
                        {
                          value: 1066 * zoomScaleFactor,
                          marker: { width: 4, ...markerStroke },
                        },
                        {
                          value: 2133 * zoomScaleFactor,
                          marker: {
                            width: 3,
                            ...markerStroke,
                            'stroke-width': 0.25,
                          },
                        },
                        {
                          marker: {
                            width: 2,
                            ...markerStroke,
                            'stroke-width': 0.1,
                          },
                        },
                      ]
                    : []),
                ]
              : style.geometryType === 'LineString'
              ? [
                  {
                    value: 133 * zoomScaleFactor,
                    line: { 'stroke-width': 5 },
                    label: showLabels
                      ? {
                          scale: style.labelScale,
                          numberDecimals: style.numberDecimals,
                        }
                      : undefined,
                    marker: {
                      width: 10,
                      fill: 'white',
                      placement: ['vertex-first'],
                      ...markerStroke,
                    },
                  },
                  {
                    value: 533 * zoomScaleFactor,
                    line: { 'stroke-width': 3 },
                    marker: {
                      width: 6,
                      fill: 'white',
                      placement: ['vertex-first'],
                      ...markerStroke,
                    },
                  },
                  {
                    value: 1066 * zoomScaleFactor,
                    line: { 'stroke-width': 2 },
                  },
                  {
                    value: 2133 * zoomScaleFactor,
                    line: { 'stroke-width': 1 },
                  },
                  {
                    line: { 'stroke-width': 0.5 },
                  },
                ]
              : [
                  {
                    value: 133 * zoomScaleFactor,
                    label: showLabels
                      ? {
                          scale: style.labelScale,
                          numberDecimals: style.numberDecimals,
                          dy: 10,
                        }
                      : undefined,
                    marker: { width: 10, ...markerStroke },
                  },
                  {
                    value: 533 * zoomScaleFactor,
                    marker: { width: 7, ...markerStroke },
                  },
                  {
                    value: 1066 * zoomScaleFactor,
                    marker: { width: 4, ...markerStroke },
                  },
                  {
                    value: 2133 * zoomScaleFactor,
                    marker: {
                      width: 3,
                      ...markerStroke,
                      'stroke-width': 0.25,
                    },
                  },
                  {
                    marker: {
                      width: 2,
                      ...markerStroke,
                      'stroke-width': 0.1,
                    },
                  },
                ],
        }
      }),
  }
}

export class MissingLayerException extends Error {}
export class EmptyLayerException extends Error {}

function scaleToQuery(scale?: number) {
  return scale != null ? { pixel_size: scale } : {}
}

function boundsToQuery(bounds?: Geometry) {
  return bounds != null ? { mask: JSON.stringify(bounds) } : {}
}

export function maskToQuery(mask?: OrthoMask) {
  if (mask != null) {
    const { type } = mask
    switch (type) {
      case 'none':
      case 'detection':
        return {}
      case 'vegetation_index':
        return {
          mask_index: `${mask.vegetationIndex}/${mask.thresholds
            .map(t => (t != null ? t : ''))
            .join(',')}`,
        }
      default:
        throw new Error(`Unsupported mask type: ${type}`)
    }
  }
}
